import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';

const LOGO_URL = '/images/intuai-logo.svg'
const LOGO_DARK_URL = '/images/intuai-logo-dark.svg'
const GRID_PATTERN_URL = '/images/auth-grid-pattern.svg'
const ILLUSTRATION_URL = '/images/auth-illustration.svg'

const TESTIMONIALS = [
  {
    quoteKey: 'common:auth.testimonial_quote',
    nameKey: 'common:auth.testimonial_name',
    roleKey: 'common:auth.testimonial_role',
    avatar: '/images/avatar-doug-raven.png',
  },
  {
    quoteKey: 'common:auth.testimonial_quote_2',
    nameKey: 'common:auth.testimonial_name_2',
    roleKey: 'common:auth.testimonial_role_2',
    avatar: '/images/avatar-uli-homann.png',
  },
  {
    quoteKey: 'common:auth.testimonial_quote_3',
    nameKey: 'common:auth.testimonial_name_3',
    roleKey: 'common:auth.testimonial_role_3',
    avatar: '/images/avatar-scott-brady.png',
  },
  {
    quoteKey: 'common:auth.testimonial_quote_4',
    nameKey: 'common:auth.testimonial_name_4',
    roleKey: 'common:auth.testimonial_role_4',
    avatar: '/images/avatar-ibrahim-asyed.png',
  },
] as const;

const ROTATION_INTERVAL_MS = 8000;

function getQuoteSizeClass(length: number): string {
  if (length < 200) return 'text-xl leading-relaxed'
  if (length < 350) return 'text-lg leading-relaxed'
  return 'text-base leading-relaxed'
}

const AuthLayout = ({children}: PropsWithChildren) => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)

  const quoteSizes = useMemo(
    () => TESTIMONIALS.map(item => getQuoteSizeClass(t(item.quoteKey).length)),
    [t],
  )

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % TESTIMONIALS.length)
    }, ROTATION_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [activeIndex])

  return (
    <div className="flex h-full">
      <div className="flex flex-1 flex-col p-10 relative">
        <img
          src={LOGO_URL}
          alt={t('common:auth.company_logo_alt')}
          className="h-9 w-auto self-start dark:hidden"
          draggable={false}
        />
        <img
          src={LOGO_DARK_URL}
          alt={t('common:auth.company_logo_alt')}
          className="h-9 w-auto self-start hidden dark:block"
          draggable={false}
        />
        <div className="flex flex-1 flex-col items-center justify-center">
          {children}
        </div>
        <p className="text-xs text-gray-500 dark:text-dark-text-muted">
          {t('common:auth.copyright')}
        </p>
      </div>
      <section className="hidden lg:block p-6 flex-1">
        <div
          className="relative h-full w-full rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(165deg, #e8590c 0%, #f07020 35%, #f5a623 100%)',
          }}
        >
          {/* Grid pattern overlay */}
          <img
            src={GRID_PATTERN_URL}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none"
            draggable={false}
          />

          {/* Decorative gradient halos */}
          <div
            className="absolute -bottom-48 -right-48 w-[600px] h-[600px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,236,187,0.35) 0%, transparent 70%)' }}
          />
          <div
            className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,236,187,0.2) 0%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-[20%] left-[30%] w-[400px] h-[400px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.15) 0%, transparent 70%)' }}
          />

          {/* Testimonial — sits above illustration via z-index */}
          <div className="relative z-20 p-10 xl:p-20">
            <div className="relative h-[420px]">
              {TESTIMONIALS.map((item, index) => (
                <div
                  key={item.nameKey}
                  className="absolute inset-0 flex flex-col justify-start transition-all duration-700 ease-in-out"
                  style={{
                    opacity: index === activeIndex ? 1 : 0,
                    transform: index === activeIndex ? 'translateY(0)' : 'translateY(12px)',
                    pointerEvents: index === activeIndex ? 'auto' : 'none',
                  }}
                  aria-hidden={index !== activeIndex}
                  {...(index !== activeIndex ? { inert: true } : {})}
                >
                  <blockquote className={`text-white/90 font-medium max-w-lg ${quoteSizes[index]}`}>
                    <span className="text-white/30 font-serif select-none" style={{ fontSize: '2em', lineHeight: 0, verticalAlign: '-0.15em' }}>{'\u201C'}</span>
                    {t(item.quoteKey)}
                    <span className="text-white/30 font-serif select-none" style={{ fontSize: '2em', lineHeight: 0, verticalAlign: '-0.15em' }}>{'\u201D'}</span>
                  </blockquote>
                  <div className="flex items-center gap-3 mt-6">
                    <img
                      src={item.avatar}
                      alt={t(item.nameKey)}
                      className="w-12 h-12 rounded-full object-cover"
                      draggable={false}
                    />
                    <div>
                      <p className="text-white font-medium text-sm">
                        {t(item.nameKey)}
                      </p>
                      <p className="text-white/70 text-sm">
                        {t(item.roleKey)}
                      </p>
                    </div>
                  </div>
                  {/* Slider dots — always right below the avatar */}
                  <div className="flex items-center gap-1.5 mt-6">
                    {TESTIMONIALS.map((dotItem, dotIndex) => (
                      <button
                        type="button"
                        key={dotItem.nameKey}
                        onClick={() => setActiveIndex(dotIndex)}
                        aria-label={`Go to testimonial ${dotIndex + 1}`}
                        className={`rounded-full transition-all duration-300 ${
                          dotIndex === activeIndex
                            ? 'w-4 h-1 bg-white'
                            : 'w-1 h-1 bg-white/40 hover:bg-white/60'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Isometric illustration — absolute bottom like production */}
          <img
            src={ILLUSTRATION_URL}
            alt=""
            className="absolute bottom-0 left-20 z-10 w-[76%] max-w-[512px] max-h-[60%] object-contain pointer-events-none"
            draggable={false}
          />
        </div>
      </section>
    </div>
  )
}

export default AuthLayout;
